import { Link } from "react-router-dom";
import { CartItemType, useHeyFlowLeadContext } from "../contexts/heyFlowLead";
import { anotherProduct, LEINEN_SAECKCHEN_PRODUCT_ID, productShops, simpleProducts } from "../utils/productTypes";
import { ProductListing } from "./ProductListing";
import { Fragment } from "react";
import { Headline } from "./Headline";

export interface OfferDetailsProps {
  heyflowId: string;
}

export function OfferDetails(props: OfferDetailsProps) {
  const { error, value, standardProductDefinitions, addCartItem } = useHeyFlowLeadContext();

  if (error) {
    return null;
  }

  if (!value || !standardProductDefinitions) {
    return null;
  }

  const { placeholderProducts } = standardProductDefinitions;

  const isSingleCremation = value.lead.lead.cremation.type === "Einzelkremierung";

  const isVorsorge = value.lead.vorsorge !== undefined;

  // Seit 30.09.2026 legt der Server das Einäscherungszertifikat als Inklusivleistung in jedes Angebot;
  // dann gehört es zu den Angebotsdetails. Ältere Angebote bieten es weiter als Option an.
  const hasIncludedCertificate = value.cart.some((cart) => cart.type === "certificate" && cart.included === true);

  const standardCategories: CartItemType[] = isSingleCremation
    ? ["cremation", "urn", "inscription", "transport", "pawPrint", "jewelry"]
    : ["cremation", "transport", "pawPrint"];
  if (isVorsorge) {
    // Guthaben als eigene, gesperrte Position sichtbar machen
    standardCategories.push("vorsorgeCredit");
  }
  if (hasIncludedCertificate) {
    standardCategories.push("certificate");
  }

  const extraCategories: CartItemType[] = [
    ...(hasIncludedCertificate ? [] : (["certificate"] as CartItemType[])),
    ...(isSingleCremation ? (["diamondConsultation"] as CartItemType[]) : []),
  ];

  const superCategories: [string, CartItemType[]][] = (
    [
      [isVorsorge ? `Details zur Einlösung deiner Vorsorge ${value.lead.offerId}` : `Details zum Angebot #${value.lead.offerId}`, standardCategories],
      ["Weitere Optionen für die Bestattung", extraCategories],
    ] as [string, CartItemType[]][]
  ).filter(([, categories]) => categories.length > 0);

  const hasProperUrn =
    value.cart.filter((cart) => cart.type === "urn" && cart.productId !== LEINEN_SAECKCHEN_PRODUCT_ID).length > 0;

  return (
    <div>
      {superCategories.map(([title, categories], superCategoryIndex) => (
        <Fragment key={superCategoryIndex}>
          <Headline title={title} type="sub" className="mb-5 mt-5" />
          <div className="mb-10">
            {categories
              .filter((category) => category !== "inscription" || hasProperUrn)
              .filter((category) => category !== "vorsorgeCredit" || value.cart.some((cart) => cart.type === "vorsorgeCredit"))
              .map((category) => (
                <Fragment key={category}>
                  {value.cart
                    .map((cartItem, index) => ({ cartItem, cartItemIndex: index }))
                    .filter(({ cartItem }) => cartItem.type === category)
                    .map(({ cartItem, cartItemIndex }) => (
                      <ProductListing
                        heyflowId={props.heyflowId}
                        key={cartItemIndex}
                        title={cartItem.name}
                        price={cartItem.price}
                        variationAttributes={cartItem.variationAttributes}
                        shortDescription={cartItem.description}
                        image={cartItem.image}
                        locked={cartItem.locked === true}
                        included={cartItem.included === true}
                        type={{
                          type: "cart",
                          cartItemIndex,
                          cartItemType: category,
                          productId: cartItem.productId,
                          variationId: cartItem.variationId,
                        }}
                        onClick={() => {
                          /**/
                        }}
                      ></ProductListing>
                    ))}
                  {value.cart.filter((cart) => cart.type === category).length === 0 ? (
                    <ProductListing
                      heyflowId={props.heyflowId}
                      title={placeholderProducts[category].name}
                      price={placeholderProducts[category].price >= 0 ? placeholderProducts[category].price : undefined}
                      shortDescription={placeholderProducts[category].description}
                      image={placeholderProducts[category].image}
                      type={{
                        type: "placeholder",
                        cartItemType: category,
                      }}
                      onClick={() => {
                        /**/
                      }}
                    ></ProductListing>
                  ) : (
                    anotherProduct[category] &&
                    (simpleProducts.indexOf(category) !== -1 ? (
                      <button
                        type="button"
                        className="text-sm mt-2 lg:mt-3 block underline"
                        onClick={() => {
                          addCartItem(placeholderProducts[category]);
                        }}
                      >
                        {anotherProduct[category]}
                      </button>
                    ) : (
                      <Link
                        className="text-sm mt-2 lg:mt-3 block underline"
                        to={productShops[category]?.(props.heyflowId) ?? ""}
                      >
                        {anotherProduct[category]}
                      </Link>
                    ))
                  )}
                </Fragment>
              ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}
